// React bindings for the i18n core. `useT()` returns a translator bound to the
// current language and re-renders its component whenever the language changes
// (via useSyncExternalStore over the core's observable store — no context is
// required for reads, so screens work in tests without a provider).
//
// `I18nProvider` loads the persisted choice from the KV store on mount, applies
// it to the native per-app locale, and exposes `changeLanguage` (persist + apply
// immediately) through context for the Language settings screen.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useAccount } from '../shell/accountContext.js';
import { isCapacitorNative } from '../shell/capacitor/platform.js';
import {
  getLang,
  getSetting,
  setLanguageSetting,
  subscribeLanguage,
  translate,
  LANG_KEY,
  type Lang,
  type LangSetting,
  type MessageKey,
  type MessageParams,
} from './core.js';

export type TFunc = (key: MessageKey, params?: MessageParams) => string;

export function useLanguage(): Lang {
  return useSyncExternalStore(subscribeLanguage, getLang, getLang);
}

export function useLanguageSetting(): LangSetting {
  return useSyncExternalStore(subscribeLanguage, getSetting, getSetting);
}

export function useT(): TFunc {
  const lang = useLanguage();
  return useMemo(
    () => (key: MessageKey, params?: MessageParams) => translate(lang, key, params),
    [lang],
  );
}

/**
 * Push the app's language choice to the native per-app locale so
 * IncomingCallActivity and the notification strings follow the in-app setting,
 * not just the system one. `null` (system) clears the override. No-op off
 * native; failures are swallowed — the locale is a nicety, never the call path.
 */
async function applyNativeLanguage(setting: LangSetting): Promise<void> {
  if (!isCapacitorNative()) return;
  try {
    const { nativePlugins } = await import('../shell/capacitor/plugins.js');
    await nativePlugins().callService.setAppLanguage({
      tag: setting === 'system' ? null : setting,
    });
  } catch {
    // Older shell / missing method: the native locale simply won't follow.
  }
}

const ChangeLanguageContext = createContext<((setting: LangSetting) => Promise<void>) | undefined>(
  undefined,
);

/**
 * The applier for the Language settings screen: updates the store immediately
 * and (inside a provider) persists + applies to native. Without a provider it
 * still switches the live language, so a screen rendered bare in tests works.
 */
export function useChangeLanguage(): (setting: LangSetting) => Promise<void> {
  const ctx = useContext(ChangeLanguageContext);
  return (
    ctx ??
    (async (setting: LangSetting) => {
      setLanguageSetting(setting);
      await applyNativeLanguage(setting);
    })
  );
}

export function I18nProvider({ children }: { children: ReactNode }): ReactNode {
  const { getKv } = useAccount();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const kv = await getKv();
        const stored = await kv.get<LangSetting>(LANG_KEY);
        if (!cancelled && (stored === 'system' || stored === 'en' || stored === 'ru')) {
          setLanguageSetting(stored);
        }
      } catch {
        // No persisted choice / storage unavailable: stay on 'system'.
      }
      await applyNativeLanguage(getSetting());
    })();
    return () => {
      cancelled = true;
    };
  }, [getKv]);

  const change = useMemo(
    () =>
      async (setting: LangSetting): Promise<void> => {
        setLanguageSetting(setting);
        try {
          const kv = await getKv();
          await kv.put(LANG_KEY, setting);
        } catch {
          // Persistence is best-effort; the live switch already took effect.
        }
        await applyNativeLanguage(setting);
      },
    [getKv],
  );

  return <ChangeLanguageContext.Provider value={change}>{children}</ChangeLanguageContext.Provider>;
}
